import { toast } from 'sonner';
import { isObjectVisual } from './FreeVisual';
import { getFieldLabel } from '@/lib/fieldLabel';

/**
 * Export the report as a multi-page PDF using jsPDF.
 * Renders header/footer zones, tables with page breaks, charts via html2canvas.
 */
export async function exportReportPdf({
  reportOrientation, reportPageSize, reportMargins, reportHeader, reportFooter,
  reportName, reportTableSpanPages, reportTableRepeatHeader,
  visuals, visualResults, allFields, getTransformedData, getVizConfig,
  parameters, paramValues, formatValue, fieldOverrides = {}
}) {
  try {
    const { default: jsPDF } = await import('jspdf');
    const doc = new jsPDF({
      orientation: reportOrientation === 'landscape' ? 'l' : 'p',
      unit: 'pt',
      format: reportPageSize === 'a4' ? 'a4' : reportPageSize === 'legal' ? 'legal' : 'letter'
    });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const mTop = reportMargins.top;
    const mLeft = reportMargins.left;
    const mBottom = reportMargins.bottom;
    const contentW = pw - mLeft - reportMargins.right;

    const renderHeader = (pageNum) => {
      if (!reportHeader.enabled) return;
      const hdrVisuals = visuals.filter(v => v._zone === 'header');
      hdrVisuals.forEach(v => {
        const fp = v.freePosition || { x: 0, y: 0 };
        if (v.vizType === 'rich_text') {
          doc.setFontSize(v.textSize || 14);
          doc.setFont(undefined, v.textBold ? 'bold' : 'normal');
          doc.setTextColor(v.textColor || '#000');
          let t = (v.textContent || '').replace(/\{\{report_name\}\}/g, reportName).replace(/\{\{page\}\}/g, String(pageNum)).replace(/\{\{date\}\}/g, new Date().toLocaleDateString());
          doc.text(t, mLeft + fp.x, mTop + fp.y + (v.textSize || 14));
        } else if (v.vizType === 'image' && v.imageUrl) {
          try {
            const fmt = v.imageUrl.startsWith('data:image/png') ? 'PNG' : v.imageUrl.startsWith('data:image/jpeg') || v.imageUrl.startsWith('data:image/jpg') ? 'JPEG' : 'PNG';
            doc.addImage(v.imageUrl, fmt, mLeft + fp.x, mTop + fp.y, fp.w || 80, fp.h || 40);
          } catch (imgErr) { console.warn('Header image failed:', imgErr); }
        }
      });
      if (hdrVisuals.length === 0 && reportHeader.title) {
        doc.setFontSize(14); doc.setFont(undefined, 'bold'); doc.setTextColor('#000');
        doc.text(reportHeader.title.replace(/\{\{report_name\}\}/g, reportName), mLeft, mTop + 16);
      }
      doc.setDrawColor('#d1d5db'); doc.setLineWidth(0.5);
      doc.line(mLeft, mTop + reportHeader.height - 4, pw - reportMargins.right, mTop + reportHeader.height - 4);
      if (parameters.length > 0) {
        doc.setFontSize(7); doc.setFont(undefined, 'normal'); doc.setTextColor('#888');
        let paramY = mTop + 34;
        parameters.forEach(p => {
          const val = paramValues[p.name] || p.defaultValue || 'All';
          doc.text(`${p.label || p.name}: ${val}`, mLeft, paramY);
          paramY += 10;
        });
      }
    };

    const totalPagesAlias = '{total_pages_placeholder}';
    const renderFooter = (pageNum) => {
      if (!reportFooter.enabled) return;
      const ftrVisuals = visuals.filter(v => v._zone === 'footer');
      ftrVisuals.forEach(v => {
        const fp = v.freePosition || { x: 0, y: 0 };
        if (v.vizType === 'rich_text') {
          doc.setFontSize(v.textSize || 9);
          doc.setTextColor(v.textColor || '#888');
          let t = (v.textContent || '').replace(/\{\{page\}\}/g, String(pageNum)).replace(/\{\{total_pages\}\}/g, totalPagesAlias).replace(/\{\{date\}\}/g, new Date().toLocaleDateString());
          doc.text(t, mLeft + fp.x, ph - mBottom + fp.y + (v.textSize || 9));
        }
      });
      if (ftrVisuals.length === 0) {
        const footerText = reportFooter.text || 'Page {{page}} of {{total_pages}}';
        doc.setFontSize(9); doc.setTextColor('#888');
        let t = footerText.replace(/\{\{page\}\}/g, String(pageNum)).replace(/\{\{total_pages\}\}/g, totalPagesAlias).replace(/\{\{date\}\}/g, new Date().toLocaleDateString());
        doc.text(t, pw / 2, ph - mBottom + 12, { align: 'center' });
      }
    };

    let curY = mTop;

    // Report header zone visuals
    const reportHdrVisuals = visuals.filter(v => v._zone === 'report_header');
    if (reportHdrVisuals.length > 0) {
      for (const v of reportHdrVisuals) {
        const fp = v.freePosition || { x: 0, y: 0, w: 200, h: 30 };
        if (v.vizType === 'rich_text') {
          doc.setFontSize(v.textSize || 14); doc.setFont(undefined, v.textBold ? 'bold' : 'normal'); doc.setTextColor(v.textColor || '#000');
          doc.text((v.textContent || '').replace(/\{\{report_name\}\}/g, reportName).replace(/\{\{date\}\}/g, new Date().toLocaleDateString()), mLeft + fp.x, curY + (v.textSize || 14));
        } else if (v.vizType === 'image' && v.imageUrl) {
          try { const fmt = v.imageUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG'; doc.addImage(v.imageUrl, fmt, mLeft + fp.x, curY + fp.y, fp.w || 80, fp.h || 30); } catch {}
        } else if (v.vizType === 'kpi') {
          const vr = (visualResults[v.id] || {}).result;
          const val = vr?.data?.[0]?.[v.valueMeasures?.[0]] ?? '--';
          doc.setFontSize(18); doc.setFont(undefined, 'bold'); doc.setTextColor('#000');
          doc.text(String(typeof val === 'number' ? val.toLocaleString() : val), mLeft + fp.x, curY + 20);
        }
      }
      curY += 50;
    }

    let pageNum = 1;
    if (reportHeader.showOnFirstPage !== false) renderHeader(pageNum);
    curY = mTop + (reportHeader.enabled && reportHeader.showOnFirstPage !== false ? reportHeader.height : 0);
    const availH = ph - mBottom - (reportFooter.enabled ? 20 : 0);

    const contentVisuals = visuals.filter(vv => !vv._zone && !isObjectVisual(vv.vizType));
    // GAP-1.5 page-break controls: honor explicit report-page boundaries
    // (`_reportPageIndex`) and per-visual `pageBreakBefore` so authors get
    // pixel-perfect control over where a new sheet starts.
    let _lastRpIndex = contentVisuals.length ? (contentVisuals[0]._reportPageIndex ?? 0) : 0;
    let _renderedAny = false;
    const _forcePageBreak = () => {
      renderFooter(pageNum); doc.addPage(); pageNum++; curY = mTop;
      if (reportHeader.enabled && reportHeader.repeatOnPages) { renderHeader(pageNum); curY += reportHeader.height; }
    };
    for (const v of contentVisuals) {
      const _rpIdx = v._reportPageIndex ?? 0;
      if (_renderedAny && (_rpIdx !== _lastRpIndex || v.pageBreakBefore)) {
        _forcePageBreak();
      }
      _lastRpIndex = _rpIdx;
      _renderedAny = true;
      const vResult = (visualResults[v.id] || {}).result;
      const vData = getTransformedData(v.id) || vResult?.data || [];

      if (v.vizType === 'table' && vData.length > 0 && reportTableSpanPages) {
        const keys = Object.keys(vData[0]).filter(k => !k.startsWith('_sort_') && !k.startsWith('_isForecast'));
        const vizCfg = getVizConfig(v.id) || {};
        // Per-table font → jsPDF built-ins (helvetica/times/courier) + size.
        const PDF_FONT_MAP = { arial: 'helvetica', helvetica: 'helvetica', verdana: 'helvetica', times: 'times', georgia: 'times', courier: 'courier' };
        const pdfFont = PDF_FONT_MAP[vizCfg.fontFamily] || 'helvetica';
        const baseFS = vizCfg.fontSize ? Number(vizCfg.fontSize) : null;
        const headerFS = baseFS ? Math.max(6, baseFS) : 8;
        const bodyFS = baseFS ? Math.max(5, baseFS - 1) : 7;
        const fmtCell = (val, k) => {
          if (typeof formatValue === 'function') { try { return String(formatValue(val, k) ?? ''); } catch { /* fall through */ } }
          return String(val != null ? (typeof val === 'number' ? val.toLocaleString() : val) : '');
        };
        const pdfColAligns = vizCfg.columnAlignments || {};
        const pdfColWidths = vizCfg.columnWidths || {};
        const totalFixed = keys.reduce((s, k) => s + (pdfColWidths[k] || 0), 0);
        const autoKeys = keys.filter(k => !pdfColWidths[k]);
        const autoW = autoKeys.length > 0 ? (contentW - totalFixed) / autoKeys.length : 0;
        const getW = (k) => pdfColWidths[k] || autoW;
        const numericCols = keys.filter(k => vData.some(r => typeof r[k] === 'number'));
        const getAlign = (k) => pdfColAligns[k] === 'right' || (pdfColAligns[k] === 'auto' && numericCols.includes(k)) ? 'right' : 'left';
        const rowH = Math.max(12, Math.round(bodyFS * 1.9));
        const headerH = Math.max(14, Math.round(headerFS * 2.1));

        const renderTableHeader = () => {
          doc.setFillColor('#e8edf2');
          doc.rect(mLeft, curY, contentW, headerH, 'F');
          doc.setFontSize(headerFS); doc.setFont(pdfFont, 'bold'); doc.setTextColor('#111');
          let cx = mLeft;
          keys.forEach(k => {
            const label = getFieldLabel(allFields.find(f => f.name === k), k, fieldOverrides[k]);
            const w = getW(k);
            if (getAlign(k) === 'right') doc.text(label, cx + w - 3, curY + headerH - 6, { align: 'right' });
            else doc.text(label, cx + 3, curY + headerH - 6);
            cx += w;
          });
          curY += headerH;
        };

        renderTableHeader();

        for (let ri = 0; ri < vData.length; ri++) {
          if (curY + rowH > availH) {
            renderFooter(pageNum);
            doc.addPage(); pageNum++;
            curY = mTop;
            if (reportHeader.enabled && reportHeader.repeatOnPages) { renderHeader(pageNum); curY += reportHeader.height; }
            if (reportTableRepeatHeader) renderTableHeader();
          }
          if (ri % 2 === 1) { doc.setFillColor('#edf2f7'); doc.rect(mLeft, curY, contentW, rowH, 'F'); }
          doc.setFontSize(bodyFS); doc.setFont(pdfFont, 'normal'); doc.setTextColor('#333');
          let cx = mLeft;
          keys.forEach(k => {
            const val = vData[ri][k];
            const w = getW(k);
            const text = fmtCell(val, k);
            if (getAlign(k) === 'right') doc.text(text, cx + w - 3, curY + rowH - 4, { align: 'right' });
            else doc.text(text, cx + 3, curY + rowH - 4);
            cx += w;
          });
          curY += rowH;
        }

        // Grand Total
        if (curY + rowH > availH) { renderFooter(pageNum); doc.addPage(); pageNum++; curY = mTop; if (reportHeader.enabled && reportHeader.repeatOnPages) { renderHeader(pageNum); curY += reportHeader.height; } }
        doc.setFillColor('#eef2ff');
        doc.rect(mLeft, curY, contentW, rowH + 2, 'F');
        doc.setDrawColor('#6366f1'); doc.setLineWidth(1);
        doc.line(mLeft, curY, mLeft + contentW, curY);
        doc.setFontSize(headerFS); doc.setFont(pdfFont, 'bold'); doc.setTextColor('#111');
        let cx = mLeft;
        let firstCol = true;
        keys.forEach(k => {
          const w = getW(k);
          if (firstCol) { doc.text('Grand Total', cx + 3, curY + rowH - 3); firstCol = false; }
          else if (numericCols.includes(k)) {
            const total = vData.reduce((s, r) => s + (typeof r[k] === 'number' ? r[k] : 0), 0);
            const text = fmtCell(total, k);
            if (getAlign(k) === 'right') doc.text(text, cx + w - 3, curY + rowH - 3, { align: 'right' });
            else doc.text(text, cx + 3, curY + rowH - 3);
          }
          cx += w;
        });
        curY += rowH + 4;
        doc.setDrawColor('#d1d5db'); doc.setLineWidth(0.5);
        doc.rect(mLeft, mTop + (reportHeader.enabled ? reportHeader.height : 0), contentW, curY - mTop - (reportHeader.enabled ? reportHeader.height : 0));
        curY += 10;

      } else if (v.vizType === 'kpi' && vData.length > 0) {
        const val = vData[0]?.[v.valueMeasures?.[0]] ?? '--';
        const label = getFieldLabel(allFields.find(f => f.name === v.valueMeasures?.[0]), v.valueMeasures?.[0] || '');
        doc.setFontSize(10); doc.setTextColor('#666'); doc.text(label, mLeft, curY + 12);
        doc.setFontSize(22); doc.setFont(undefined, 'bold'); doc.setTextColor('#000');
        doc.text(String(typeof val === 'number' ? val.toLocaleString() : val), mLeft, curY + 32);
        curY += 50;
      } else {
        const selectors = [`[data-visual-id="${v.id}"]`, `.react-grid-item [data-visual-id="${v.id}"]`];
        let chartEl = null;
        for (const sel of selectors) { chartEl = document.querySelector(sel); if (chartEl) break; }
        if (!chartEl) { const allItems = document.querySelectorAll('[data-visual-id]'); for (const item of allItems) { if (item.getAttribute('data-visual-id') === v.id) { chartEl = item; break; } } }
        if (chartEl && chartEl.offsetHeight > 0) {
          try {
            chartEl.scrollIntoView({ block: 'nearest' });
            await new Promise(r => setTimeout(r, 300));
            const rechartsEl = chartEl.querySelector('.recharts-responsive-container') || chartEl.querySelector('.recharts-wrapper') || chartEl;
            const origOverflow = chartEl.style.overflow;
            const origHeight = chartEl.style.height;
            const origMinHeight = chartEl.style.minHeight;
            chartEl.style.overflow = 'visible';
            chartEl.style.height = 'auto';
            chartEl.style.minHeight = '300px';
            const origRechartsH = rechartsEl.style.height;
            if (rechartsEl !== chartEl) rechartsEl.style.height = '300px';
            await new Promise(r => setTimeout(r, 500));
            const { default: html2canvas } = await import('html2canvas');
            const chartCanvas = await html2canvas(chartEl, { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true, allowTaint: true, width: chartEl.scrollWidth, height: Math.max(chartEl.scrollHeight, 300) });
            chartEl.style.overflow = origOverflow;
            chartEl.style.height = origHeight;
            chartEl.style.minHeight = origMinHeight;
            if (rechartsEl !== chartEl) rechartsEl.style.height = origRechartsH;
            const chartImg = chartCanvas.toDataURL('image/png');
            const aspect = chartCanvas.height / chartCanvas.width;
            const chartH = Math.min(350, Math.max(200, contentW * aspect));
            if (curY + chartH > availH) { renderFooter(pageNum); doc.addPage(); pageNum++; curY = mTop; if (reportHeader.enabled && reportHeader.repeatOnPages) { renderHeader(pageNum); curY += reportHeader.height; } }
            doc.addImage(chartImg, 'PNG', mLeft, curY, contentW, chartH);
            curY += chartH + 10;
          } catch (chartErr) {
            console.warn('Chart capture failed for', v.id, chartErr);
            doc.setFontSize(9); doc.setTextColor('#999');
            doc.text(`[${v.vizType}: ${v.title || ''} - capture failed]`, mLeft, curY + 12);
            curY += 30;
          }
        } else {
          if (v.vizType === 'image' && v.imageUrl) {
            try {
              const fmt = v.imageUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
              const imgH = 100;
              if (curY + imgH > availH) { renderFooter(pageNum); doc.addPage(); pageNum++; curY = mTop; if (reportHeader.enabled && reportHeader.repeatOnPages) { renderHeader(pageNum); curY += reportHeader.height; } }
              doc.addImage(v.imageUrl, fmt, mLeft, curY, Math.min(contentW, 200), imgH);
              curY += imgH + 10;
            } catch { curY += 30; }
          } else {
            doc.setFontSize(9); doc.setTextColor('#999');
            doc.text(`[${v.vizType}: ${v.title || ''}]`, mLeft, curY + 12);
            curY += 30;
          }
        }
        curY += 30;
      }
    }

    // Report footer zone visuals
    const reportFtrVisuals = visuals.filter(v => v._zone === 'report_footer');
    if (reportFtrVisuals.length > 0) {
      if (curY + 50 > availH) { renderFooter(pageNum); doc.addPage(); pageNum++; curY = mTop; if (reportHeader.enabled && reportHeader.repeatOnPages) { renderHeader(pageNum); curY += reportHeader.height; } }
      for (const v of reportFtrVisuals) {
        const fp = v.freePosition || { x: 0, y: 0, w: 200, h: 20 };
        if (v.vizType === 'rich_text') {
          doc.setFontSize(v.textSize || 10); doc.setFont(undefined, v.textBold ? 'bold' : 'normal'); doc.setTextColor(v.textColor || '#333');
          doc.text((v.textContent || '').replace(/\{\{report_name\}\}/g, reportName).replace(/\{\{date\}\}/g, new Date().toLocaleDateString()), mLeft + fp.x, curY + (v.textSize || 10));
        }
      }
      curY += 40;
    }

    renderFooter(pageNum);
    doc.putTotalPages(totalPagesAlias);
    doc.save(`${reportName || 'report'}.pdf`);
    toast.success('PDF exported');
  } catch (err) {
    toast.error('PDF export failed: ' + err.message);
  }
}
